import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  getStorageKeyPipelines,
  getStorageKeyCurrentId,
  getActiveCrmId,
} from "../deals/usePipelines";

export const ACTIVE_CRM_STORAGE_KEY = "dotus_active_crm_id";
export const DEFAULT_CRM_ID = "fe386f56-b27f-446f-86ec-39a03b64add1"; // Grupo Dotus

export interface ActiveCRM {
  id: string;
  name: string;
  slug: string;
  status: string;
  role?: "owner" | "admin" | "member" | "superadmin";
  settings?: Record<string, any>;
  voip_enabled?: boolean;
  transcription_enabled?: boolean;
}

interface CRMContextType {
  currentCrm: ActiveCRM | null;
  userCrms: ActiveCRM[];
  isSuperadmin: boolean;
  isLoading: boolean;
  isImpersonating: boolean;
  isVoipEnabled: boolean;
  isTranscriptionEnabled: boolean;
  switchCrm: (crmId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  refreshCrms: () => Promise<void>;
}

const CRMContext = createContext<CRMContextType | undefined>(undefined);

export const CRMProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentCrm, setCurrentCrm] = useState<ActiveCRM | null>(null);
  const [userCrms, setUserCrms] = useState<ActiveCRM[]>([]);
  const [isSuperadmin, setIsSuperadmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchCrmsAndUser = useCallback(async (isSilent = false) => {
    if (!isSilent) {
      setIsLoading(true);
    }
    try {
      const client = getSupabaseClient();
      const { data: sessionData } = await client.auth.getSession();
      const user = sessionData?.session?.user;

      if (!user) {
        setIsLoading(false);
        return;
      }

      // 1. Verifica se usuário é Superadmin na tabela sales
      const { data: saleData } = await client
        .from("sales")
        .select("id, is_superadmin, administrator")
        .eq("user_id", user.id)
        .maybeSingle();

      const superadmin = Boolean(saleData?.is_superadmin);
      setIsSuperadmin(superadmin);

      let availableCrms: ActiveCRM[] = [];

      if (superadmin) {
        // Superadmin tem visibilidade de todos os CRMs
        const { data: allCrms } = await client
          .from("crms")
          .select("id, name, slug, status, settings")
          .order("created_at", { ascending: true });

        if (allCrms && allCrms.length > 0) {
          availableCrms = allCrms.map((c) => ({
            id: c.id,
            name: c.name,
            slug: c.slug,
            status: c.status,
            role: "superadmin",
            settings: c.settings || {},
            voip_enabled: Boolean(c.settings?.voip_enabled),
            transcription_enabled: Boolean(c.settings?.transcription_enabled),
          }));
        }
      } else {
        // Usuário padrão: busca seus CRMs via crm_members (suporta user_id e sales_id)
        let query = client
          .from("crm_members")
          .select("crm_id, role, crms(id, name, slug, status, settings)");

        if (saleData?.id) {
          query = query.or(`user_id.eq.${user.id},sales_id.eq.${saleData.id}`);
        } else {
          query = query.eq("user_id", user.id);
        }

        const { data: memberRows } = await query;

        if (memberRows && memberRows.length > 0) {
          availableCrms = memberRows
            .filter((m: any) => m.crms)
            .map((m: any) => ({
              id: m.crms.id,
              name: m.crms.name,
              slug: m.crms.slug,
              status: m.crms.status,
              role: m.role,
              settings: m.crms.settings || {},
              voip_enabled: Boolean(m.crms.settings?.voip_enabled),
              transcription_enabled: Boolean(
                m.crms.settings?.transcription_enabled,
              ),
            }));
        }
      }

      // Se nenhum CRM encontrado
      if (availableCrms.length === 0) {
        if (superadmin) {
          availableCrms = [
            {
              id: DEFAULT_CRM_ID,
              name: "Dotus CRM Principal",
              slug: "principal",
              status: "active",
              role: "superadmin",
            },
          ];
        } else {
          // O CRM do usuário foi excluído ou o usuário não possui vínculo com nenhum CRM
          setCurrentCrm(null);
          setUserCrms([]);
          localStorage.removeItem(ACTIVE_CRM_STORAGE_KEY);
          setIsLoading(false);
          return;
        }
      }

      setUserCrms(availableCrms);

      // Determina CRM Ativo
      const savedCrmId = localStorage.getItem(ACTIVE_CRM_STORAGE_KEY);
      const isInvalidSaved =
        !savedCrmId || savedCrmId === "00000000-0000-0000-0000-000000000001";
      const matched = !isInvalidSaved
        ? availableCrms.find((c) => c.id === savedCrmId)
        : null;
      const dotusCrm =
        availableCrms.find((c) => c.id === DEFAULT_CRM_ID) ||
        availableCrms.find((c) => c.slug === "grupo-dotus");

      const active = matched || dotusCrm || availableCrms[0];
      setCurrentCrm(active);
      localStorage.setItem(ACTIVE_CRM_STORAGE_KEY, active.id);
    } catch (err) {
      console.warn("Erro ao carregar CRMs no CRMProvider:", err);
      // Fallback seguro sem quebrar
      const fallback: ActiveCRM = {
        id: DEFAULT_CRM_ID,
        name: "Grupo Dotus",
        slug: "grupo-dotus",
        status: "active",
      };
      setCurrentCrm(fallback);
      setUserCrms([fallback]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCrmsAndUser();

    const client = getSupabaseClient();
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, session) => {
      if (
        event === "SIGNED_IN" ||
        event === "USER_UPDATED" ||
        event === "TOKEN_REFRESHED"
      ) {
        if (session?.user) {
          fetchCrmsAndUser(true);
        }
      } else if (event === "SIGNED_OUT") {
        setCurrentCrm(null);
        setUserCrms([]);
        setIsSuperadmin(false);
        setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchCrmsAndUser]);

  const switchCrm = async (crmId: string) => {
    // Limpa o cache de pipelines do CRM ATUAL antes de trocar
    // Isso evita que pipelines de um CRM apareçam brevemente em outro CRM
    const previousCrmId = getActiveCrmId();
    if (previousCrmId && previousCrmId !== crmId) {
      // Não apagamos os dados do CRM anterior — apenas marcamos para revalidação
      // O novo CRM terá seus dados carregados do Supabase no próximo mount
      try {
        // Remove a chave do NOVO CRM se ela tiver dados velhos (do CRM anterior)
        // Isso força um reload limpo do Supabase para o novo CRM
        const newCrmPipelinesKey = getStorageKeyPipelines(crmId);
        const newCrmCurrentIdKey = getStorageKeyCurrentId(crmId);
        // Se o localStorage do novo CRM tem dados que podem ser do CRM errado,
        // limpamos para garantir que o Supabase seja a fonte de verdade
        localStorage.removeItem(newCrmPipelinesKey);
        localStorage.removeItem(newCrmCurrentIdKey);
      } catch {}
    }
    localStorage.setItem(ACTIVE_CRM_STORAGE_KEY, crmId);
    const target = userCrms.find((c) => c.id === crmId);
    if (target) {
      setCurrentCrm(target);
    } else {
      // Se for superadmin e CRM não estiver no cache local
      try {
        const client = getSupabaseClient();
        const { data } = await client
          .from("crms")
          .select("id, name, slug, status, settings")
          .eq("id", crmId)
          .single();

        if (data) {
          const newActive: ActiveCRM = {
            id: data.id,
            name: data.name,
            slug: data.slug,
            status: data.status,
            role: isSuperadmin ? "superadmin" : "member",
            settings: data.settings || {},
            voip_enabled: Boolean(data.settings?.voip_enabled),
            transcription_enabled: Boolean(
              data.settings?.transcription_enabled,
            ),
          };
          setCurrentCrm(newActive);
          setUserCrms((prev) =>
            prev.some((c) => c.id === data.id) ? prev : [...prev, newActive],
          );
        }
      } catch (e) {
        console.error("Falha ao trocar para o CRM:", e);
      }
    }
    // Dispara recarregamento para sincronizar dados e cache do react-admin
    window.location.reload();
  };

  const exitImpersonation = async () => {
    localStorage.removeItem(ACTIVE_CRM_STORAGE_KEY);
    await switchCrm(DEFAULT_CRM_ID);
  };

  const isImpersonating = Boolean(
    isSuperadmin && currentCrm && currentCrm.id !== DEFAULT_CRM_ID,
  );

  const isVoipEnabled = Boolean(
    currentCrm?.settings?.voip_enabled ?? currentCrm?.voip_enabled,
  );
  const isTranscriptionEnabled = Boolean(
    currentCrm?.settings?.transcription_enabled ??
      currentCrm?.transcription_enabled,
  );

  return (
    <CRMContext.Provider
      value={{
        currentCrm,
        userCrms,
        isSuperadmin,
        isLoading,
        isImpersonating,
        isVoipEnabled,
        isTranscriptionEnabled,
        switchCrm,
        exitImpersonation,
        refreshCrms: fetchCrmsAndUser,
      }}
    >
      {children}
    </CRMContext.Provider>
  );
};

export const useOptionalCRM = () => {
  return useContext(CRMContext);
};

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error("useCRM deve ser utilizado dentro de um CRMProvider");
  }
  return context;
};
